import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Save, Edit, User, X, CheckCircle, Circle } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useStore } from "@/lib/store";
import type { UserClone } from "@shared/schema";

interface InsertUserClone {
  title: string;
  styleAnalysis: string;
}

export function UserCloneModal() {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editingClone, setEditingClone] = useState<UserClone | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { userCloneModalOpen, setUserCloneModalOpen } = useStore();

  // Fetch user clones
  const { data: userClones, isLoading } = useQuery<UserClone[]>({
    queryKey: ["/api/user-clones"],
  });

  // Fetch active user clone
  const { data: activeUserClone } = useQuery<UserClone | null>({
    queryKey: ["/api/user-clones/active"],
  });

  // Create user clone mutation
  const createMutation = useMutation({
    mutationFn: async (data: InsertUserClone) => {
      return await apiRequest("POST", "/api/user-clones", data);
    },
    onSuccess: (newClone) => {
      queryClient.invalidateQueries({ queryKey: ["/api/user-clones"] });
      setIsEditing(false);
      setEditingClone(null);
      toast({
        title: t("userClone.toast.created"),
        description: t("userClone.toast.createdDesc"),
      });
    },
    onError: () => {
      toast({
        title: t("userClone.toast.retryTitle"),
        description: t("userClone.toast.retryDesc"),
        variant: "destructive",
      });
    },
  });

  // Update user clone mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: InsertUserClone }) => {
      return await apiRequest("PUT", `/api/user-clones/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user-clones"] });
      setIsEditing(false);
      setEditingClone(null);
      toast({
        title: t("userClone.toast.updated"),
        description: t("userClone.toast.updatedDesc"),
      });
    },
    onError: () => {
      toast({
        title: t("userClone.toast.retryTitle"),
        description: t("userClone.toast.retryDesc"),
        variant: "destructive",
      });
    },
  });

  // Delete user clone mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return await apiRequest("DELETE", `/api/user-clones/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user-clones"] });
      queryClient.invalidateQueries({ queryKey: ["/api/user-clones/active"] });
      toast({
        title: t("userClone.toast.deleted"),
        description: t("userClone.toast.deletedDesc"),
      });
    },
    onError: () => {
      toast({
        title: t("userClone.toast.retryTitle"),
        description: t("userClone.toast.retryDesc"),
        variant: "destructive",
      });
    },
  });

  // Activate user clone mutation
  const activateMutation = useMutation({
    mutationFn: async (id: string) => {
      return await apiRequest("PUT", `/api/user-clones/${id}/activate`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user-clones"] });
      queryClient.invalidateQueries({ queryKey: ["/api/user-clones/active"] });
      toast({
        title: t("userClone.toast.activated"),
        description: t("userClone.toast.activatedDesc"),
      });
    },
    onError: () => {
      toast({
        title: t("userClone.toast.retryTitle"),
        description: t("userClone.toast.retryDesc"),
        variant: "destructive",
      });
    },
  });

  // Deactivate user clone mutation
  const deactivateMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("PUT", "/api/user-clones/deactivate");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user-clones"] });
      queryClient.invalidateQueries({ queryKey: ["/api/user-clones/active"] });
      toast({
        title: t("userClone.toast.deactivated"),
        description: t("userClone.toast.deactivatedDesc"),
      });
    },
    onError: () => {
      toast({
        title: t("userClone.toast.retryTitle"),
        description: t("userClone.toast.retryDesc"),
        variant: "destructive",
      });
    },
  });

  // Save style from clone-yourself module
  const saveStyleFromModuleMutation = useMutation({
    mutationFn: async () => {
      // Get latest conversations from "clone-yourself" module
      const conversations = await apiRequest("GET", "/api/conversations");
      const cloneConversations = conversations.filter(
        (conv: any) => conv.moduleId === "clone-yourself"
      );

      if (cloneConversations.length === 0) {
        throw new Error(t("userClone.errors.noConversations"));
      }

      // Get latest conversation
      const latestConversation = cloneConversations.sort(
        (a: any, b: any) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      )[0];

      // Get messages from that conversation
      const messages = await apiRequest(
        "GET",
        `/api/conversations/${latestConversation.id}/messages`
      );
      const aiMessages = messages.filter((msg: any) => msg.role === "assistant");

      if (aiMessages.length === 0) {
        throw new Error(t("userClone.errors.noAnalysis"));
      }

      // Get the latest AI response
      const latestAiResponse = aiMessages[aiMessages.length - 1];

      // Create user clone with the analysis
      return await apiRequest("POST", "/api/user-clones", {
        title: t("userClone.myStyleTitle"),
        styleAnalysis: latestAiResponse.content,
      });
    },
    onSuccess: (newClone) => {
      queryClient.invalidateQueries({ queryKey: ["/api/user-clones"] });
      queryClient.invalidateQueries({ queryKey: ["/api/user-clones/active"] });

      // Activate the newly created clone
      apiRequest("PUT", `/api/user-clones/${newClone.id}/activate`).then(() => {
        queryClient.invalidateQueries({ queryKey: ["/api/user-clones/active"] });
      });

      toast({
        title: t("userClone.toast.saved"),
        description: t("userClone.toast.savedDesc"),
      });
    },
    onError: (error: any) => {
      toast({
        title: t("userClone.toast.saveAgainTitle"),
        description: t("userClone.toast.retryDesc"),
        variant: "destructive",
      });
    },
  });

  const handleCreateNew = () => {
    setEditingClone(null);
    setIsEditing(true);
  };

  const handleEdit = (clone: UserClone) => {
    setEditingClone(clone);
    setIsEditing(true);
  };

  const handleSave = (formData: FormData) => {
    const data: InsertUserClone = {
      title: (formData.get("title") as string) || t("userClone.defaultTitle"),
      styleAnalysis: (formData.get("styleAnalysis") as string) || "",
    };

    if (editingClone) {
      updateMutation.mutate({ id: editingClone.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (id: string) => {
    if (confirm(t("userClone.confirmDelete"))) {
      deleteMutation.mutate(id);
    }
  };

  const handleActivate = (id: string) => {
    // If clicking on already active clone, deactivate it
    if (activeUserClone?.id === id) {
      deactivateMutation.mutate();
    } else {
      activateMutation.mutate(id);
    }
  };

  const closeModal = () => {
    setUserCloneModalOpen(false);
    setIsEditing(false);
    setEditingClone(null);
  };

  if (!userCloneModalOpen) return null;

  return (
    <Dialog open={userCloneModalOpen} onOpenChange={closeModal}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-hidden p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="text-heading-2 font-extrabold">
            {t("userClone.title")}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 h-full min-h-0">
          {/* Left side - List of styles */}
          <div className="w-1/2 p-4 border-r overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{t("userClone.yourStyles")}</h3>
              <div className="flex gap-2">
                <Button onClick={handleCreateNew} size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  {t("userClone.new")}
                </Button>
              </div>
            </div>

            {activeUserClone && (
              <p className="text-sm text-muted-foreground mb-4">
                {t("userClone.active")}:{" "}
                <span className="text-green-600 font-medium">{activeUserClone.title}</span>
              </p>
            )}

            {isLoading ? (
              <div className="text-center py-8">{t("userClone.loading")}</div>
            ) : (
              <div className="space-y-3">
                {userClones && userClones.length > 0 ? (
                  userClones.map((clone) => (
                    <Card
                      key={clone.id}
                      className={`cursor-pointer transition-all hover:shadow-md ${
                        activeUserClone?.id === clone.id ? "ring-2 ring-green-500 bg-green-50" : ""
                      }`}
                      onClick={() => handleActivate(clone.id)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            {activeUserClone?.id === clone.id ? (
                              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                            ) : (
                              <Circle className="h-5 w-5 text-gray-400 flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium truncate">{clone.title}</h4>
                              <p className="text-sm text-muted-foreground truncate">
                                {clone.styleAnalysis.substring(0, 100)}...
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 flex-shrink-0">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(clone);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(clone.id);
                              }}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <User className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{t("userClone.empty")}</p>
                    <p className="text-sm mt-1">{t("userClone.emptyHint")}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right side - Form */}
          <div className="w-1/2 p-4 overflow-y-auto">
            {isEditing ? (
              <Card>
                <CardHeader>
                  <CardTitle>
                    {editingClone ? t("userClone.editTitle") : t("userClone.newTitle")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      const formData = new FormData(e.currentTarget);
                      handleSave(formData);
                    }}
                  >
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="title">{t("userClone.styleNameLabel")}</Label>
                        <Input
                          id="title"
                          name="title"
                          defaultValue={editingClone?.title || ""}
                          placeholder={t("userClone.styleNamePlaceholder")}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="styleAnalysis">{t("userClone.styleAnalysisLabel")}</Label>
                        <Textarea
                          id="styleAnalysis"
                          name="styleAnalysis"
                          defaultValue={editingClone?.styleAnalysis || ""}
                          placeholder={t("userClone.styleAnalysisPlaceholder")}
                          rows={12}
                          className="resize-none w-full"
                          required
                        />
                      </div>
                    </div>

                    <div className="flex gap-2 mt-6">
                      <Button
                        type="submit"
                        className="flex-1"
                        disabled={createMutation.isPending || updateMutation.isPending}
                      >
                        <Save className="h-4 w-4 mr-2" />
                        {t("userClone.save")}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setIsEditing(false);
                          setEditingClone(null);
                        }}
                      >
                        {t("userClone.cancel")}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <User className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="text-sm">{t("userClone.selectToEdit")}</p>
                <p className="text-xs">{t("userClone.selectHint")}</p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
